import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/library/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/library/select";
import {
  CalendarClock,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
} from "lucide-react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { cn } from "@/lib/utils";
import {
  registerTransitionFollowUpPrompt,
  type FollowUpPromptOptions,
} from "@/lib/transitionNoteGate";
import { useFollowUpTimeEnabled } from "@/features/leads/api/useLeadFormConfig";
import { followUpMaxDate } from "@/features/leads/utils/followUpMaxDays";

/**
 * Globally mounted "Follow-up required" dialog (see AppProvider) — the
 * follow-up twin of `TransitionNoteHost`.
 *
 * A stage/status move that hits a "FollowUps" requirement is rejected with
 * `followUpRequired`; the axios interceptor holds that request's promise,
 * opens this dialog, then writes the follow-up through the normal follow-up
 * endpoint and replays the move. Cancelling surfaces the original error.
 *
 * Renders the drawer calendar's month grid inline (same cells, colours and
 * disabled rules as `DatePickerCalendar`) rather than a bare `<input
 * type="date">` — the picker itself is the Xale look, and it is the ONE
 * place this popup differs from the drawer. Mounted app-wide, so it reads
 * no lead/deal form state: the only inputs are the gate's message and the
 * "Maximum days allowed" cap the server ships with the 400
 * (`followUpMaxDays`), which greys out the days the write would bounce on.
 * When the tenant captures follow-up TIMES the dialog adds an hour / minute
 * row; otherwise the date is emitted at local noon (the server flattens it
 * to 00:00 IST, and a UTC-midnight instant would land on the previous day
 * for anyone west of IST).
 */

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES_5 = Array.from({ length: 12 }, (_, i) => i * 5);
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const plural = (n: number, word: string) =>
  `${n} ${word}${n === 1 ? "" : "s"}`;

export default function TransitionFollowUpHost() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [maxDays, setMaxDays] = useState<number | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(() => new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [hour, setHour] = useState(10);
  const [minute, setMinute] = useState(0);
  const [period, setPeriod] = useState<"AM" | "PM">("AM");
  const resolveRef = useRef<((iso: string | null) => void) | null>(null);
  const enableTime = useFollowUpTimeEnabled();

  const settle = useCallback((value: string | null) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setOpen(false);
  }, []);

  useEffect(() => {
    registerTransitionFollowUpPrompt(
      (msg, opts?: FollowUpPromptOptions) =>
        new Promise<string | null>((resolve) => {
          resolveRef.current = resolve;
          const now = new Date();
          setMessage(msg);
          setMaxDays(opts?.maxDays ?? null);
          setCurrentMonth(now);
          setSelectedDate(null);
          setHour(10);
          setMinute(0);
          setPeriod("AM");
          setOpen(true);
        }),
    );
    return () => registerTransitionFollowUpPrompt(null);
  }, []);

  const today = startOfDay(new Date());
  const maxDate = useMemo(() => followUpMaxDate(maxDays), [maxDays]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    return eachDayOfInterval({
      start: startOfWeek(monthStart),
      end: endOfWeek(monthEnd),
    });
  }, [currentMonth]);

  const isPastDate = (d: Date) => isBefore(startOfDay(d), today);
  const isBeyondMax = (d: Date) =>
    maxDate != null && isAfter(startOfDay(d), startOfDay(maxDate));
  const isDisabledDay = (d: Date) => isPastDate(d) || isBeyondMax(d);

  // Every day of the month shown is disabled → the arrow in that direction
  // leads nowhere useful, so it is disabled too (mirrors the drawer).
  const prevMonthAllPast = isBefore(
    endOfMonth(subMonths(currentMonth, 1)),
    today,
  );
  const nextMonthAllBeyond =
    maxDate != null &&
    isAfter(startOfMonth(addMonths(currentMonth, 1)), startOfDay(maxDate));

  const to24h = () => {
    let h = hour % 12;
    if (period === "PM") h += 12;
    return h;
  };
  const timePast = (() => {
    if (!enableTime || !selectedDate || !isSameDay(selectedDate, today))
      return false;
    const now = new Date();
    const h = to24h();
    return h < now.getHours() || (h === now.getHours() && minute <= now.getMinutes());
  })();

  const canSave = !!selectedDate && !isDisabledDay(selectedDate) && !timePast;

  const submit = () => {
    if (!selectedDate || !canSave) return;
    const d = new Date(selectedDate);
    if (enableTime) {
      d.setHours(to24h(), minute, 0, 0);
    } else {
      // Local noon, not midnight — see the header comment.
      d.setHours(12, 0, 0, 0);
    }
    settle(d.toISOString());
  };

  const maxHint =
    maxDays != null && maxDate
      ? maxDays === 0
        ? "Only today is allowed here."
        : `Up to ${plural(maxDays, "day")} out is allowed here — until ${format(maxDate, "dd MMM yyyy")}.`
      : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && settle(null)}>
      <DialogContent className="sm:max-w-[420px] rounded-xl p-0 overflow-hidden gap-0">
        <DialogHeader className="px-5 pt-5 pb-0">
          {/* <p>, not DialogTitle — Radix's <h2> picks up the global heading size. */}
          <p className="flex items-center gap-2 text-base font-semibold text-[#051912]">
            <CalendarClock className="size-4 text-[#218760]" />
            Follow-up required
          </p>
          <Separator className="mt-3" />
          <DialogDescription className="pt-2 text-[12.5px] leading-snug text-[#697571]">
            {message || "Schedule a follow-up to make this change."}
          </DialogDescription>
        </DialogHeader>

        <div className="px-3 pt-2">
          {/* Month header */}
          <div className="flex items-center justify-between pl-2 pr-1 py-1">
            <span className="text-[14px] font-medium text-[#49454f] tracking-[-0.56px]">
              {format(currentMonth, "MMMM yyyy")}
            </span>
            <div className="flex items-center">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={prevMonthAllPast}
                className="size-10 rounded-full hover:bg-transparent disabled:opacity-30"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                aria-label="Previous month"
              >
                <ChevronLeft className="size-5 text-[#49454f]" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={nextMonthAllBeyond}
                className="size-10 rounded-full hover:bg-transparent disabled:opacity-30"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                aria-label="Next month"
              >
                <ChevronRight className="size-5 text-[#49454f]" />
              </Button>
            </div>
          </div>

          {/* Weekday row */}
          <div className="grid grid-cols-7 gap-px px-1">
            {WEEKDAYS.map((d, i) => (
              <div key={`wd-${i}`} className="size-10 flex items-center justify-center">
                <span className="text-[14px] font-normal text-black tracking-[-0.56px] leading-[1.35]">
                  {d}
                </span>
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-px px-1">
            {calendarDays.map((day, index) => {
              const inMonth = isSameMonth(day, currentMonth);
              const isToday = isSameDay(day, today);
              const isSelected = !!selectedDate && isSameDay(day, selectedDate);
              const disabled = isDisabledDay(day);
              return (
                <button
                  key={`d-${format(day, "yyyy-MM-dd")}-${index}`}
                  type="button"
                  disabled={disabled}
                  title={
                    isBeyondMax(day)
                      ? (maxHint ?? "Beyond the allowed follow-up window")
                      : undefined
                  }
                  onClick={() => {
                    if (disabled) return;
                    if (!inMonth) setCurrentMonth(startOfMonth(day));
                    setSelectedDate(day);
                  }}
                  className={cn(
                    "size-10 flex items-center justify-center rounded-full transition-colors",
                    !inMonth || disabled ? "text-[#9ba3a0]" : "text-[#1e302a]",
                    isSelected && !isToday && "bg-[#218760] text-white",
                    isSelected && isToday && "bg-[#218760] text-white border border-[#218760]",
                    isToday && !isSelected && "border border-[#218760] text-[#218760]",
                    disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                    !isSelected && !isToday && !disabled && "hover:bg-gray-100",
                  )}
                >
                  <span className="text-[14px] font-normal tracking-[-0.56px] leading-[1.35]">
                    {format(day, "d")}
                  </span>
                </button>
              );
            })}
          </div>

          {maxHint && (
            <p className="mx-1 mt-2 flex items-center gap-1.5 rounded-[10px] bg-[#f4f7f6] px-2.5 py-1.5 text-[11px] leading-snug text-[#697571]">
              <CalendarDays className="size-3.5 shrink-0 text-[#218760]" />
              <span>{maxHint}</span>
            </p>
          )}

          {/* Time row (tenants that capture follow-up times) */}
          {enableTime && (
            <div
              className={cn(
                "mx-1 mt-2 flex items-center justify-between gap-2 rounded-[12px] border px-3 py-2",
                timePast
                  ? "border-red-200 bg-red-50/60"
                  : "border-[#e6e8e7] bg-[#f7f9f8]",
              )}
            >
              <div className="flex items-center gap-2">
                <Clock className="size-4 text-[#218760]" />
                <span className="text-[12px] font-medium text-[#1e302a]">Time</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Select value={String(hour)} onValueChange={(v) => setHour(Number(v))}>
                  <SelectTrigger className="h-8 w-[64px] rounded-lg bg-white border border-[#e6e8e7] text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS_12.map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-[14px] font-medium text-[#1e302a]">:</span>
                <Select value={String(minute)} onValueChange={(v) => setMinute(Number(v))}>
                  <SelectTrigger className="h-8 w-[68px] rounded-lg bg-white border border-[#e6e8e7] text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MINUTES_5.map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {String(m).padStart(2, "0")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center rounded-lg bg-white border border-[#e6e8e7] p-0.5">
                  {(["AM", "PM"] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPeriod(p)}
                      className={cn(
                        "px-2 py-1 rounded-md text-[11px] font-medium transition-colors",
                        period === p
                          ? "bg-[#218760] text-white"
                          : "text-[#697571] hover:bg-[#f7f9f8]",
                      )}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {timePast && (
            <p className="mx-1 mt-1 text-[11px] font-medium text-[#EF5350]">
              This time has already passed today.
            </p>
          )}
        </div>

        <DialogFooter className="mt-3 flex items-center justify-between gap-2 border-t border-[#e6e8e7] px-5 py-3 sm:justify-between">
          <span className="text-[12px] text-[#697571] truncate">
            {selectedDate
              ? enableTime
                ? `${format(selectedDate, "EEE, dd MMM yyyy")} · ${hour}:${String(minute).padStart(2, "0")} ${period}`
                : format(selectedDate, "EEE, dd MMM yyyy")
              : "Pick a date"}
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <Button type="button" variant="cancel" onClick={() => settle(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={!canSave}
              onClick={submit}
            >
              Save &amp; continue
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
